
import { mapGetters } from 'vuex'
import { name as toName } from '~/util/string'
import { sentAtText } from '~/util/summary-copy'
import { saveSummaryOnScreen } from '~/util/summary-save'

export default {
    props: {
        summary: {
            type: Object,
            default: null
        },
        client: {
            type: Object,
            default: null
        },
        // the newest frozen copy (GET /summary/{id}/copy/: date, by), or null
        // for a summary never sent
        lastSend: {
            type: Object,
            default: null
        },
    },
    data () {
        return {
            sending: false,
            // a repeat send waits for "Send again"
            confirmRepeat: false,
            form: {
                // #685: default ON for AEP-flagged summaries; the send payload
                // only carries the key when the summary is AEP (see send()).
                link_only: !!this.$store.getters.summaryAep,
                // unticked to re-send a correction to staff alone
                cc_agent: true,
                to: this.client?.email || '',
                subject: `${this.$store.getters.canAgent ? this.$store.getters.summaryAep ? `AEP Recommendation: ${toName(this.client, { skipPreferred: true })}` : `Medicare Overview: ${toName(this.client, { skipPreferred: true })}` : `${toName(this.client, { skipPreferred: true })}'s ` + (this.$store.getters.summaryAep ? `AEP Plan Summary ${this.$store.getters.year}` : `Medicare Overview for ${this.summary.year}`)}`,
                light: true,
                message: `
                        <p>Dear ${this.$_.startCase(this.client.preferred_name || this.client.first_name)},</p>

                        ${this.$store.getters.summaryAep
        ? `<p>Thank you for providing us with your updated prescription and health information! I want to pass along our plan analysis for ${this.$store.getters.year} (attached below).</p>`
        : this.$store.getters.canAgent
            ? '<p>It was wonderful speaking with you today! I would like to follow up after our consultation and provide you with the following documents (attached):</p>'
            : '<p>We would like to follow up our online consultation and provide you with the following documents (attached):</p>'
}
            ${!this.$store.getters.summaryAep
        ? `<ul>
                            <li>Medicare Consultation Summary</li>
                            <li>Plan Benefit Brochures</li>
                        </ul>`
        : ''
}

                        <p>
                            Please don't hesitate to reach out if you have any questions. ${new Date().getDay() < 5 ? 'Hope you have a wonderful rest of your week!' : 'Hope you have a wonderful weekend!'}
                        <br>
                        <p>
                            <span>${this.$store.getters.canAgent ? toName(this.$store.getters.user, { skipPreferred: true }) : 'The Doctor\'s Choice Team'}</span>
                            </br>
                            <span>Office: <a target="_blank" rel="noopener noreferrer nofollow" href="tel:+14014047373">401-404-7373</a></span>
                            </br>                          
                            <span>Email: <a target="_blank" rel="noopener noreferrer nofollow" href="mailto:${this.$store.getters.canAgent ? this.$store.getters.user.email : 'support@doctorschoiceusa.com'}">${this.$store.getters.canAgent ? this.$store.getters.user.email : 'support@doctorschoiceusa.com'}</a></span>
                        </p>
                `
            },
        }
    },
    computed: {
        ...mapGetters(['summaryAep', 'summaryPlansList']),
        // #685: the notification-only send is active — the backend ignores
        // subject/light/message on that path, so those controls hide.
        linkOnlyActive () {
            return !!this.summaryAep && !!this.form.link_only
        },
        clientFirstName () {
            return this.client?.preferred_name || this.client?.first_name || ''
        },
        // Mirrors the backend's aep_link_season_end: sent Sep 1 – Dec 8, the
        // link lasts through Dec 8 (and at least 30 days).
        linkLife () {
            const d = new Date()
            const inSeason = d.getMonth() >= 8 && !(d.getMonth() === 11 && d.getDate() > 8)
            return inSeason ? 'The link works through December 8.' : 'The link expires after 30 days.'
        },
        emails () {
            return (this.form.to || '').split(',').map(to => to.trim()).filter(to => !!to)
        },
        // The client's assigned agent (the one the backend CCs + notifies), if active.
        assignedAgent () {
            const agent = this.client?.agent
            return agent && agent.active ? agent : null
        },
        // True when a staffer other than the client's assigned agent is sending: the
        // agent — not the sender — gets the CC + notification.
        senderIsNotAssignedAgent () {
            return this.canAgent && this.assignedAgent && this.assignedAgent.id !== this.user?.id
        },
        myEmailInTo () {
            const email = (this.user?.email || '').trim().toLowerCase()
            return !!email && this.emails.some(to => to.toLowerCase() === email)
        },
        possibleNonDownloadableFilesPlans () {
            return this.summaryPlansList.filter(plan => /humana/i.test(plan.carrier) || /humana/i.test(plan.name))
        },
        // A copy rendered after the fact (a summary sent before copies existed,
        // 2026-10-03) is dated by its render and "by" whoever ran the command:
        // the send is the summary's own `sent`, and nobody is named.
        lastSentDate () {
            if (!this.lastSend) return null
            return (this.lastSend.kind === 'rendered' ? this.summary?.sent : this.lastSend.date) || null
        },
        editedSince () {
            const updated = this.summary?.updated
            return !!this.lastSentDate && !!updated && this.$moment(updated).isAfter(this.lastSentDate)
        },
        lastSendLine () {
            if (!this.lastSentDate) return ''
            const by = this.lastSend.kind !== 'rendered' && this.lastSend.by ? ` by ${this.lastSend.by}` : ''
            return `Sent ${sentAtText(this.lastSentDate, this.$moment)}${by}${this.editedSince ? ', edited since' : ''}`
        },
        sentAgo () {
            return this.lastSentDate ? this.$moment(this.lastSentDate).fromNow() : ''
        },
    },
    methods: {
        addMyEmail () {
            const email = this.user?.email
            if (!email || this.myEmailInTo) return
            this.form.to = this.emails.concat(email).join(', ')
        },
        async send () {
            if (this.sending) return
            if (this.lastSentDate && !this.confirmRepeat) {
                this.confirmRepeat = true
                return
            }
            this.sending = true
            try {
                // the client's copy renders the saved summary: save the screen first
                if (!(await saveSummaryOnScreen(this, this.summary.id))) {
                    return
                }
                const { error } = await this.$try('sendSummary', {
                    id: this.summary.id,
                    data: {
                        ...this.form,
                        to: this.emails,
                        // #685: only AEP-flagged summaries send the flag —
                        // undefined keys are dropped by axios, so non-AEP
                        // payloads stay byte-identical.
                        link_only: this.summaryAep ? !!this.form.link_only : undefined,
                    }
                })
                if (!error) {
                    this.$success(`Summary ${this.canAgent ? this.summary.id + ' ' : ' '}sent!`)
                    this.$emit('success')
                }
            } finally {
                this.sending = false
                this.confirmRepeat = false
            }
        },
        sendAgain () {
            this.confirmRepeat = true
            return this.send()
        },
    },
}
